import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Check, Shield, Clock, FileText, Download, ChevronDown, ChevronUp } from 'lucide-react';

const RECIPIENT_ROLES = [
  { value: 'accountant', label: 'Accountant' }, { value: 'tax_advisor', label: 'Tax Advisor' },
  { value: 'family_office', label: 'Family Office Manager' }, { value: 'estate_planner', label: 'Estate Planner' },
  { value: 'other', label: 'Other' },
];

export const RecipientStep = ({ recipientName, setRecipientName, recipientEmail, setRecipientEmail, recipientRole, setRecipientRole }) => (
  <div className="space-y-5" data-testid="share-step-1">
    <h3 className="text-base font-semibold text-slate-900">Recipient Details</h3>
    <div className="space-y-4">
      <div>
        <Label htmlFor="recipientName">Recipient Name</Label>
        <Input id="recipientName" data-testid="share-recipient-name" value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="e.g. John Smith" className="mt-1" />
      </div>
      <div>
        <Label htmlFor="recipientEmail">Recipient Email</Label>
        <Input id="recipientEmail" data-testid="share-recipient-email" type="email" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="e.g. john@accounting.com" className="mt-1" />
      </div>
      <div>
        <Label>Role</Label>
        <Select value={recipientRole} onValueChange={setRecipientRole}>
          <SelectTrigger className="mt-1" data-testid="share-recipient-role"><SelectValue placeholder="Select role" /></SelectTrigger>
          <SelectContent>{RECIPIENT_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  </div>
);

export const ScopeStep = ({ scope, setScope, expandedScope, setExpandedScope, scopeOptions, assetClassLabels, selectedAssetClasses, toggleAssetClass, entities, selectedEntities, toggleEntity }) => (
  <div className="space-y-5" data-testid="share-step-2">
    <h3 className="text-base font-semibold text-slate-900">What would you like to share?</h3>
    <div className="space-y-3">
      {scopeOptions.map(opt => (
        <div key={opt.value} className="border rounded-lg overflow-hidden">
          <button type="button" data-testid={`share-scope-${opt.value}`} className={`w-full text-left p-4 flex items-start gap-3 transition-colors ${scope === opt.value ? 'bg-emerald-50 border-emerald-200' : 'hover:bg-slate-50'}`} onClick={() => { setScope(opt.value); setExpandedScope(expandedScope === opt.value ? null : opt.value); }}>
            <div className={`mt-0.5 ${scope === opt.value ? 'text-emerald-600' : 'text-slate-400'}`}>{opt.icon}</div>
            <div className="flex-1">
              <div className="flex items-center gap-2"><span className="font-medium text-slate-900">{opt.label}</span>{scope === opt.value && <Check className="w-4 h-4 text-emerald-600" />}</div>
              <p className="text-sm text-slate-500 mt-0.5">{opt.description}</p>
            </div>
            {opt.expandable && scope === opt.value && <div className="mt-0.5 text-slate-400">{expandedScope === opt.value ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</div>}
          </button>
          {opt.value === 'asset_classes' && scope === 'asset_classes' && expandedScope === 'asset_classes' && (
            <div className="p-4 pt-0 bg-emerald-50 border-t border-emerald-100">
              <div className="grid grid-cols-2 gap-2 mt-3">
                {Object.entries(assetClassLabels).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 p-2 rounded bg-white border cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={selectedAssetClasses.includes(key)} onChange={() => toggleAssetClass(key)} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {opt.value === 'entities' && scope === 'entities' && expandedScope === 'entities' && (
            <div className="p-4 pt-0 bg-emerald-50 border-t border-emerald-100">
              {entities.length > 0 ? (
                <div className="space-y-2 mt-3">
                  {entities.map(e => (
                    <label key={e.entity_id} className="flex items-center gap-2 p-2 rounded bg-white border cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={selectedEntities.includes(e.entity_id)} onChange={() => toggleEntity(e.entity_id)} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                      <span className="text-sm font-medium">{e.entity_name}</span>
                      <Badge variant="outline" className="text-xs">{e.entity_type}</Badge>
                    </label>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-500 mt-3">No entities found. Create holding entities first.</p>}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

export const PermissionsStep = ({ allowPdfView, setAllowPdfView, allowSummaryExport, setAllowSummaryExport, branding, setBranding }) => (
  <div className="space-y-5" data-testid="share-step-3">
    <h3 className="text-base font-semibold text-slate-900">Access Permissions</h3>
    <div className="p-4 bg-slate-50 rounded-lg border">
      <div className="flex items-center gap-2 mb-3"><Shield className="w-4 h-4 text-slate-600" /><span className="font-medium text-sm text-slate-700">Default Settings</span></div>
      <div className="space-y-1 text-sm text-slate-600"><p>Read-only access</p><p>No downloads</p><p>No exports</p></div>
    </div>
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div className="flex items-center gap-3"><FileText className="w-5 h-5 text-slate-500" /><div><p className="font-medium text-sm">Allow PDF View</p><p className="text-xs text-slate-500">Recipient can view portfolio as a formatted PDF</p></div></div>
        <Switch data-testid="share-allow-pdf" checked={allowPdfView} onCheckedChange={setAllowPdfView} />
      </div>
      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div className="flex items-center gap-3"><Download className="w-5 h-5 text-slate-500" /><div><p className="font-medium text-sm">Allow Summary Export</p><p className="text-xs text-slate-500">Recipient can export a portfolio summary</p></div></div>
        <Switch data-testid="share-allow-export" checked={allowSummaryExport} onCheckedChange={setAllowSummaryExport} />
      </div>
    </div>
    <div className="space-y-3">
      <Label>Branding</Label>
      <RadioGroup value={branding} onValueChange={setBranding} className="flex gap-4">
        <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="myinvestfolio" id="brand-mif" /><span className="text-sm">MyInvestFolio Branded</span></label>
        <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="whitelabel" id="brand-wl" /><span className="text-sm">Clean / White-label</span></label>
      </RadioGroup>
    </div>
  </div>
);

export const DurationStep = ({ durationDays, setDurationDays, customExpiry, setCustomExpiry, durationOptions }) => (
  <div className="space-y-5" data-testid="share-step-4">
    <h3 className="text-base font-semibold text-slate-900">Access Duration</h3>
    <p className="text-sm text-slate-500">Access will automatically expire at the end of the selected period, unless extended.</p>
    <div className="space-y-2">
      {durationOptions.map(opt => (
        <button key={opt.value} type="button" data-testid={`share-duration-${opt.value}`} className={`w-full text-left p-3 rounded-lg border flex items-center gap-3 transition-colors ${durationDays === opt.value ? 'bg-emerald-50 border-emerald-300' : 'hover:bg-slate-50'}`} onClick={() => setDurationDays(opt.value)}>
          <Clock className={`w-4 h-4 ${durationDays === opt.value ? 'text-emerald-600' : 'text-slate-400'}`} />
          <span className={`font-medium text-sm ${durationDays === opt.value ? 'text-emerald-700' : 'text-slate-700'}`}>{opt.label}</span>
          {durationDays === opt.value && <Check className="w-4 h-4 text-emerald-600 ml-auto" />}
        </button>
      ))}
    </div>
    {durationDays === 'custom' && (
      <div>
        <Label htmlFor="customExpiry">Expiry Date</Label>
        <Input id="customExpiry" data-testid="share-custom-expiry" type="date" value={customExpiry} onChange={e => setCustomExpiry(e.target.value)} min={new Date(Date.now() + 86400000).toISOString().split('T')[0]} className="mt-1" />
      </div>
    )}
  </div>
);

export const ReviewStep = ({ recipientName, recipientEmail, getRoleLabel, getScopeLabel, getDurationLabel, scope, selectedAssetClasses, assetClassLabels, selectedEntities, entities, allowPdfView, allowSummaryExport, branding }) => (
  <div className="space-y-5" data-testid="share-step-5">
    <h3 className="text-base font-semibold text-slate-900">Review & Confirm</h3>
    <div className="space-y-3">
      <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
        <div className="flex justify-between"><span className="text-sm text-slate-500">Recipient</span><span className="text-sm font-medium">{recipientName}</span></div>
        <div className="flex justify-between"><span className="text-sm text-slate-500">Email</span><span className="text-sm font-medium">{recipientEmail}</span></div>
        <div className="flex justify-between"><span className="text-sm text-slate-500">Role</span><span className="text-sm font-medium">{getRoleLabel()}</span></div>
        <div className="flex justify-between"><span className="text-sm text-slate-500">Scope</span><span className="text-sm font-medium">{getScopeLabel()}</span></div>
        {scope === 'asset_classes' && selectedAssetClasses.length > 0 && (
          <div className="flex justify-between items-start"><span className="text-sm text-slate-500">Asset Classes</span>
            <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">{selectedAssetClasses.map(c => <Badge key={c} variant="outline" className="text-xs">{assetClassLabels[c]}</Badge>)}</div>
          </div>
        )}
        {scope === 'entities' && selectedEntities.length > 0 && (
          <div className="flex justify-between items-start"><span className="text-sm text-slate-500">Entities</span>
            <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">{selectedEntities.map(id => { const ent = entities.find(e => e.entity_id === id); return <Badge key={id} variant="outline" className="text-xs">{ent?.entity_name || id}</Badge>; })}</div>
          </div>
        )}
        <div className="flex justify-between"><span className="text-sm text-slate-500">Duration</span><span className="text-sm font-medium">{getDurationLabel()}</span></div>
        <div className="flex justify-between"><span className="text-sm text-slate-500">PDF View</span><span className="text-sm font-medium">{allowPdfView ? 'Allowed' : 'Not Allowed'}</span></div>
        <div className="flex justify-between"><span className="text-sm text-slate-500">Summary Export</span><span className="text-sm font-medium">{allowSummaryExport ? 'Allowed' : 'Not Allowed'}</span></div>
        <div className="flex justify-between"><span className="text-sm text-slate-500">Branding</span><span className="text-sm font-medium">{branding === 'myinvestfolio' ? 'MyInvestFolio' : 'White-label'}</span></div>
      </div>
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800"><Shield className="w-4 h-4 inline mr-1.5 -mt-0.5" />You are granting secure, read-only access. You may revoke this access at any time.</p>
      </div>
    </div>
  </div>
);
